import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { api } from '../api/client';
import { useSite } from '../context/SiteContext';

const Shop = () => {
  const [params, setParams] = useSearchParams();
  const cat = params.get('cat') || 'all';
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('featured');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { categories } = useSite();

  useEffect(() => {
    setLoading(true);
    api.listProducts({ published: true, ...(cat !== 'all' ? { category: cat } : {}) })
      .then((data) => setProducts(data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [cat]);

  const list = useMemo(() => {
    let l = [...products];
    if (q) l = l.filter((p) => (p.name + ' ' + (p.flavor || '')).toLowerCase().includes(q.toLowerCase()));
    if (sort === 'price-asc') l.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') l.sort((a, b) => b.price - a.price);
    if (sort === 'name') l.sort((a, b) => a.name.localeCompare(b.name));
    return l;
  }, [products, q, sort]);

  return (
    <>
      <Header />
      <main className="bg-black min-h-screen">
        <div className="max-w-[1480px] mx-auto px-6 pt-16 pb-10">
          <p className="text-[#d4af37] tracking-[0.4em] text-xs uppercase mb-3">Shop</p>
          <h1 className="serif-italic gold-gradient-text text-5xl md:text-6xl">All Products</h1>
          <p className="text-zinc-400 mt-3 max-w-xl">Explore our full range of authentic RandM Fumot Tornado disposables, fresh inventory delivered fast.</p>
        </div>

        <div className="max-w-[1480px] mx-auto px-6 grid lg:grid-cols-[260px,1fr] gap-10">
          <aside className="space-y-6">
            <div>
              <h4 className="serif-italic text-[#d4af37] text-xl mb-3">Categories</h4>
              <ul className="space-y-2">
                <li><button onClick={() => setParams({})} className={`text-sm hover:text-[#d4af37] ${cat==='all'?'text-[#d4af37]':'text-zinc-300'}`}>All Products</button></li>
                {categories.map((c) => (
                  <li key={c.id}><button onClick={() => setParams({ cat: c.slug })} className={`text-sm hover:text-[#d4af37] ${cat===c.slug?'text-[#d4af37]':'text-zinc-300'}`}>{c.name}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="serif-italic text-[#d4af37] text-xl mb-3">Search</h4>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Flavor or name…" className="bg-transparent border-zinc-800" />
            </div>
            <div>
              <h4 className="serif-italic text-[#d4af37] text-xl mb-3">Sort by</h4>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="bg-transparent border-zinc-800 text-zinc-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price-asc">Price: Low → High</SelectItem>
                  <SelectItem value="price-desc">Price: High → Low</SelectItem>
                  <SelectItem value="name">Name (A–Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setParams({}); setQ(''); setSort('featured'); }} variant="outline" className="btn-outline-gold w-full">Reset filters</Button>
          </aside>

          <div>
            <p className="text-zinc-400 text-sm mb-6">{loading ? 'Loading…' : <>Showing <span className="text-[#d4af37]">{list.length}</span> products</>}</p>
            {(!loading && list.length === 0) ? (
              <p className="text-zinc-500">No products match your filters.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {list.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Shop;
